import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { agentService } from '../../services/agentService';

interface InviteAgentModalProps {
  businessProfileId: string;
  onClose: () => void;
  onSuccess: () => void;
}

export function InviteAgentModal({
  businessProfileId,
  onClose,
  onSuccess,
}: InviteAgentModalProps) {
  const { t } = useTranslation();
  const { token } = useAuth();
  const { showToast } = useToast();

  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!email.trim()) {
      setError(t('validation.required'));
      return;
    }

    if (!validateEmail(email)) {
      setError(t('validation.invalidEmail'));
      return;
    }

    if (!token) return;

    setIsSubmitting(true);
    try {
      await agentService.inviteAgent(email, businessProfileId, token);
      showToast('success', t('agents.inviteSuccess'), t('agents.inviteSuccessMessage', { email }));
      onSuccess();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to send invitation';
      setError(message);
      showToast('error', t('common.error'), message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60">
      <div className="bg-brand-primary-deep rounded-xl w-full max-w-md shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
          <h2 className="text-xl font-semibold text-white">{t('agents.inviteAgent')}</h2>
          <button
            onClick={onClose}
            className="p-2 text-white/70 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6">
          <div className="mb-4">
            <label className="block text-sm font-medium text-white/70 mb-2">
              {t('agents.emailLabel')}
            </label>
            <input
              type="email"
              value={email}
              onChange={e => {
                setEmail(e.target.value);
                setError(null);
              }}
              placeholder={t('agents.emailPlaceholder')}
              className={`w-full px-4 py-3 bg-surface-card border rounded-lg text-ink-primary placeholder-ink-subtle focus:outline-none focus:ring-2 ${
                error ? 'border-brand-warm focus:ring-brand-warm/50' : 'border-border-strong focus:ring-brand-primary/50'
              }`}
              autoFocus
            />
            {error && (
              <p className="mt-2 text-sm text-brand-warm">{error}</p>
            )}
          </div>

          <p className="text-sm text-white/60 mb-6">
            {t('agents.inviteDescription')}
          </p>

          {/* Actions */}
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 bg-white/10 border border-white/30 text-white rounded-lg hover:bg-white/20 transition-colors"
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 px-4 py-2 bg-brand-primary text-white rounded-lg hover:bg-brand-primary-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
            >
              {isSubmitting && (
                <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
              )}
              {t('agents.sendInvitation')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
